import { VoyageRoutingModelBase, VoyageRoute, RouteCalcStatus, RoutingJobOptions, 
         VoyageRoutePointFeature, VoyageRouteLineFeature, WeatherLimits, CalculationFailedDetails, Backends, NextWaypointResult, NextWaypointPayload, MissingPoints, 
         FeatureOrigin,
         RoutingModelEvents} from './voyage-routing-model';
import { RoutingService } from '../services/routing-service';
import { UiStateModel } from '../models/ui-state-model';
import { VesselPositionModel } from "../models/vessel-position-model";
import { LineString } from 'ol/geom';
import { getLineStringForFractionPositions } from '../utils/map-utils';
import { compareRoutes, debounce, getEdittedStarRouteName, GetFormattedLocalDate, getNamesOfRoutesFromPlan, hasPilotEtaChanged, UUID, isOnline } from '../utils/helpers';
import { WeatherModel } from './weather-model';
import { DefaultShipModel } from "./ship-model";

import { RouteOptimizerService } from "../services/route-optimiser-service";
import { PlanChangedEvent, VesselPlanningModel, VoyagePlan } from './vessel-planning-model';
import { HydraRoutingService } from '../services/hydra-routing-service';
import { RouteWeatherWorker } from '../utils/route-weather-worker';
import { _checkRouteAlignment, addPrefillValuesToPassagePlan, checkIfRequestedEtaWithin110PercentOfRequestedEta, hasRouteEdits, markPassedAndAddCurrentPosition, verifyRequestedETAs, verifyWarningsFromCalculation } from '../utils/route-utils';
import { WhaleZonesUtilities } from '../utils/zones/whale-zones-utils';
import { getZoneCrossingSignature } from '../utils/zone-crossing-utils';
import { EcaSecaAreasUtilities } from '../utils/zones/eca-seca-areas-utils';
import { EncService } from '../services/enc-service';
import { VesselEnpBookAndChapterModel } from './vessel-enp-book-and-chapter-model';
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { importFromFile } from '../converters/converters';
import { ImportResult } from '../converters/types/converters.types';
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types';
import { faro } from '../utils/rum-utility';
import { VoyageImoModel } from './voyage-imo-model';

const _PROJECTION: string = (<any>window)["appenv"].projection;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
interface UIRouteMetadata
{
    routeId: string;
    name: string;
    status: RouteCalcStatus;
    routeIndex: number;
    error: null|string;
    selected: boolean;
    crossingWhaleZones: boolean;
}

interface VesselMovementCacheEntry
{
    startDate: number;
    endDate: number;
    line: LineString;
    projectedLine: LineString;
}

export interface JobDetails
{
    jobId: string|number;
    calcOptions?: RoutingJobOptions
}

export class RouteOptimiserCalcError extends Error
{
    readonly hydraRequestId: string;
    readonly hydraErrorBody: any;
    
    constructor(hydraRequestId: string, hydraErrorBody: any, message: string)
    {
        super(message);

        this.hydraRequestId = hydraRequestId;
        this.hydraErrorBody = hydraErrorBody;
    }
}

export class VoyageRoutingModelImpl extends VoyageRoutingModelBase
{
    shipModel = DefaultShipModel;

    constructor()
    {
        super();

        this._backend = (<any>window)["appenv"].featureToggles.routingBackend || "DTN";

        this._emitRoutesChanged = debounce( () => this.dispatchEvent(new Event(SuggestedRouteEvents.RoutesChanged)) );
        this._loadWeatherLimits();
        this._checkForURLJobs();

        this._createUIRoutes(5); // max 5 routes for now

        this._onPlanChanged = this._onPlanChanged.bind(this);
        if (VesselPlanningModel)
        {
            VesselPlanningModel.addEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanChanged);
        }
    }
    private _hideRouteArray: string[] = [];

    get activePlan()
    {
        return this._activePlan;
    }

    get jobCount() {
        return this._jobs.length;
    }

    get routes() {
        return this._routes;
    }

    get uiRoutes()
    {
        const result = [];

        for (const uiRoute of this._uiRoutes)
        {
            result.push({
                routeId: uiRoute.routeId,
                name: uiRoute.name,
                status: uiRoute.status,
                route: uiRoute.routeIndex !== -1 ? this.routes[uiRoute.routeIndex] : null,
                error: uiRoute.error,
                selected : uiRoute.selected,
                crossingWhaleZones: uiRoute.crossingWhaleZones,
            });
        }

        return result;
    }

    get routingBackend()
    {
        return this._backend;
    }

    etaDeviationPopupIsShown(scheduleId: string, routeIndex: number) {
        return this._openEtaDeviationWarnings.has(`${scheduleId}_${routeIndex}`);
    }

    warningPopupIsShown(scheduleId: string, routeIndex: number) {
        return this._openWarnings.has(`${scheduleId}_${routeIndex}`);
    }

    setEtaDeviationPopupIsShown(scheduleId: string, routeIndex: number, status: "close"|"open") {
        const key = `${scheduleId}_${routeIndex}`;
        if (status === "open") {
            this._openEtaDeviationWarnings.add(key);
        } else {
            this._openEtaDeviationWarnings.delete(key);
        }
    }

    setWarningPopupIsShown(scheduleId: string, routeIndex: number, status: "close"|"open") {
        const key = `${scheduleId}_${routeIndex}`;
        if (status === "open") {
            this._openWarnings.add(key);
        } else {
            this._openWarnings.delete(key);
        }
    }

    changeRoutingBackend(newBackend: Backends)
    {
        this._backend = newBackend;
    }

    async setActivePlan(scheduleId: string): Promise<void>
    {
        if (!scheduleId)
        {
            this._activePlan = null;
            this._clearUIRoutes();
        }
        else if (scheduleId !== this._activePlan)
        {
            this._activePlan = scheduleId;
            return this._updateRoutesFromPlan();
        }
    }

    addRoute(route: VoyageRoute): number {

        this.routes.push(route);
        this._emitRoutesChanged();

        return this.routes.length - 1;
    }

    getRoute(index: number) {
        return this.routes[index];
    }

    deleteRoute(index: number)
    {
        this.routes.splice(index, 1);

        for (const uiRoute of this._uiRoutes)
        {
            if (uiRoute.routeIndex === index)
            {
                uiRoute.routeIndex = -1;
                uiRoute.status = "idle";
                uiRoute.selected = false;
            }
            else if (uiRoute.routeIndex > index)
            {
                uiRoute.routeIndex--;
            }
        }

        this._emitRoutesChanged();
    }

    async postRoutingJob(startPos: [number, number], endPos: [number, number], options: RoutingJobOptions): Promise<VoyageRoute>
    {
        const route: VoyageRoute = {
            routeId: options.routeId || "",
            route_geometry: [
                {
                    type: "Feature",
                    geometry: { coordinates: startPos, type: "Point" },
                    properties: { type: "Hard" }
                },
                {
                    type: "Feature",
                    geometry: { coordinates: [startPos, endPos], type: "LineString" },
                    properties: {}
                },
                {
                    type: "Feature",
                    geometry: { coordinates: endPos, type: "Point" },
                    properties: { type: "Hard" }
                }
            ]
        };

        return await this.postRoutingJobUsingRoute(route, options);
    }

    private _checkIfweNeedToCalclateTwice(route: VoyageRoute, options: RoutingJobOptions, plan: VoyagePlan): boolean {
        //check if first waypoint is_passed is false or null then do it twice
        const firstWaypoint = route.route_geometry[0];
        if (plan.etd < new Date().getTime() && firstWaypoint && firstWaypoint.properties && (!firstWaypoint.properties.isPassed || firstWaypoint.properties.isPassed === false)) {
            return true;
        }
        return false;
    }

    private _currentPositionOutdated(positionTimestamp: number | null | undefined): boolean {
        const acceptedTime = 1000 * 60 * 60;

        if (positionTimestamp == null) {  // covers both null and undefined, not 0
            return true;
        }
        return positionTimestamp + acceptedTime < Date.now();
    }

    private _getStartTimeOfOptimisation(plan: VoyagePlan, options: RoutingJobOptions): number {
        // If recalculation from start of voyage, return plan etd
        if (options.shouldRecalculate) {
            return plan.etd;
        }

        const optionsEtd = options.etd ?? plan.etd;

        // Only use vessel position for ongoing voyages (ETD in the past)
        if (plan.etd < Date.now()) {
            const position = VesselPositionModel!.position;
            const positionTimestamp = position?.timestamp
                ? new Date(position.timestamp).getTime()
                : null;
            if (positionTimestamp) {
                return positionTimestamp;
            }

            // for ongoing voyage and position is not available, use current time
            if (Date.now() > optionsEtd) {
                return Date.now();
            }
        }

        return optionsEtd;
    }


    async postRoutingJobUsingRoute(route: VoyageRoute, options: RoutingJobOptions, noVerifyEtaPopUp?: boolean): Promise<VoyageRoute>
    {
        if ( isVesselOnCloud && !isOnline() && !options.recalcParametersUpdate) {
            return {} as VoyageRoute;
        }
        let calculatedRoute: VoyageRoute;

        if (!options.draft && !(options.draftAft && options.draftFore)) {
            throw new Error("Missing draft in input to postRoutingJob");
        }

        const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
        if (!plan)
        {
            throw new Error(`No plan exists for ${options.planScheduleId}!`);
        }

        const isDoubleCalculationRequired = this._checkIfweNeedToCalclateTwice(route, options, plan);
        options.shouldRecalculate = isDoubleCalculationRequired;

        // latest postion's timestamp for ongoing voyage, or etd of voyage in case it's a future voyage
        const etd = this._getStartTimeOfOptimisation(plan, options);
        // if on going voyage and position ( timestamp in current vessel position) is outdated, show pop up and do not calculate route
        if (!options.shouldRecalculate && (plan.etd < Date.now()) && this._currentPositionOutdated(etd) && isOnline()) {
            VoyageDialogPopupManager.createDialog({
                headerText: "Vessel position outdated",
                bodyText: "Vessel position is too old. Please input the vessels current position first."
            });
            return {} as VoyageRoute;
        }

        options.weatherLimits = options.weatherLimits || this._weatherLimits;
        options.etd = etd;
        
        
        try
        {
            switch (this._backend)
            {
                case Backends.DTN:
                    calculatedRoute = await this._postRoutingJobToRoutingAPI(route, options);
                    break;

                case Backends.RouteOptimiser:
                    calculatedRoute = await this._postRoutingJobToOptimiser(route, options);
                    break;

                case Backends.Hydra:
                    calculatedRoute = await this._postRoutingJobToHydra(route, options);
                    break;
                
                default:
                    throw new Error("Invalid backend!")
            }

            if(isVesselOnCloud && !isOnline() && options.recalcParametersUpdate){
                return calculatedRoute;
            }

            if(!noVerifyEtaPopUp){
                const etaWarningSignature = this._buildEtaWarningSignature(
                    calculatedRoute,
                    plan,
                    options.planScheduleId || undefined,
                    options?.uiRouteIndex,
                );
                if (!etaWarningSignature || !this._warningSignatureIsRecent(etaWarningSignature)) {
                    await verifyRequestedETAs(calculatedRoute,plan, options.planScheduleId || undefined, options?.uiRouteIndex);
                }

                const calculationWarningSignature = this._buildCalculationWarningSignature(
                    calculatedRoute,
                    options.planScheduleId || undefined,
                    options?.uiRouteIndex,
                );
                if (!calculationWarningSignature || !this._warningSignatureIsRecent(calculationWarningSignature)) {
                    await verifyWarningsFromCalculation(calculatedRoute, plan, options.planScheduleId || undefined, options?.uiRouteIndex);
                }
            }

            this._detectZones(calculatedRoute, options.uiRouteIndex!);
            
            return calculatedRoute;
        }
        catch (e)
        {
            console.warn("Calculation failed!", e);

            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = (<Error>e).message;
                this._emitRoutesChanged();
            }

            this.dispatchEvent(new CustomEvent<CalculationFailedDetails>("calculation-failed", {
                detail: {
                    error: (<Error>e).message,
                    routeIndex: options.uiRouteIndex!,
                    scheduleId: options.updatePlan ? options.planScheduleId : undefined,
                    hydraRequestId: e instanceof RouteOptimiserCalcError ? e.hydraRequestId : undefined,
                    hydraErrorBody: e instanceof RouteOptimiserCalcError ? e.hydraErrorBody : undefined
                }
            }));

            throw e;
        }
    }

    async postEncAugmentRouteJob(route: VoyageRoute): Promise<VoyageRoute|null> {
        const routeWithEnc = await EncService.postEncAugmentRouteJob(route);
        return routeWithEnc;
    }

    setWeatherLimits(weatherLimits: WeatherLimits): void
    {
        this._weatherLimits = weatherLimits;
        localStorage.setItem("voyage-routing-model-weather-limits", JSON.stringify(weatherLimits));
        this.dispatchEvent(new Event(SuggestedRouteEvents.WeatherLimitsChanged));
    }

    getWeatherLimits(): WeatherLimits
    {
        return this._weatherLimits;
    }

    planHasActiveCalculations(scheduleId: string): boolean
    {
        return this._jobs.some( job => job.calcOptions && job.calcOptions.planScheduleId === scheduleId );
    }

    async importFilesRaw(files: FileList): Promise<ImportResult[]>
    {
        const promises: Promise<ImportResult>[] = [];

        Array.from(files).forEach(file => {
            promises.push( importFromFile(file) );
        });

        return Promise.all(promises);
    }

    async importFile(file: File, options: RoutingJobOptions, points: MissingPoints | null,  mergeWith?: VoyageRoute, addCurrentPosition?: boolean): Promise<VoyageRoute>
    {
        let imported: ImportResult;

        if (options.updatePlan)
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
            if (!plan)
            {
                throw new Error(`No plan exists for ${options.planScheduleId}!`);
            }

            imported = await importFromFile(file, plan, mergeWith);
            
            if ( points && points.beforeFistPilotPoint && points?.afterLastPilotPoint ) {
       
                const pointsBeforeDepPilotPoint = points.beforeFistPilotPoint.filter(el => !el.properties.pilotPoint );
                const pointsAfterArrPilotPoint = points.afterLastPilotPoint.filter(el => !el.properties.pilotPoint );
                const routeFromPilotToPilot = imported.route.route_geometry.map((el, idx )=> {
      
                    if(idx === 0 || idx === imported.route.route_geometry.length -1 )  {

                        const properties = {...el.properties,  pilotPoint: true}
                        return {...el, properties}
                    }
                    return el;
                })
                
                const route = Array.from(new Set([...pointsBeforeDepPilotPoint, ...routeFromPilotToPilot, ...pointsAfterArrPilotPoint]));
                const updatedRoute = route.map(feature => {
                    if ('type' in feature.properties && feature.properties.type === 'Hard') {
                         return {...feature, properties: {...feature.properties, origin: FeatureOrigin.Bridge}};
                     } else {
                        return feature;
                     }
                })
                
                const route_g = {...imported.route, route_geometry: updatedRoute}
                imported = {...imported, route: route_g };
                
           }


            // Apply extras (if any) to plan as well
            if (imported.extras)
            {
                plan.departurePilotPosition = imported.extras.departurePilotPosition || plan.departurePilotPosition;
                plan.arrivalPilotPosition = imported.extras.arrivalPilotPosition || plan.arrivalPilotPosition;
                if (imported.extras.passagePlan)
                {
                    for (const prop in imported.extras.passagePlan)
                    {
                        // Skipping drafts as it should not be taken from uploaded routes
                        if (prop === 'drafts') {
                            continue;
                        }
                        if (prop in plan.passagePlan)
                        {
                            (<any>plan.passagePlan[<keyof VoyagePlan["passagePlan"]>prop]) = imported.extras.passagePlan[prop];
                        }
                    }
                }
            }

            // Add current position if needed
            if (addCurrentPosition && plan.etd < new Date().getTime())
            {
                await markPassedAndAddCurrentPosition(imported.route, true, true);
            }

            // Save route to plan now (so we still have it if the calculation fails for some reason)
            if (options.updatePlan.selectRoute === undefined) {
                options.updatePlan.selectRoute = options.updatePlan.routeIndex >= 0 && plan.selectedRouteIndex === options.updatePlan.routeIndex;
            }
            
            if (options.uiRouteIndex) {
                imported.route.uiType = `UserRoute${options.uiRouteIndex}`;
            }

            if(options.routeId) {
                imported.route.routeId = options.routeId;
            }
            
            if (options.updatePlan.routeIndex >= 0) {
                plan.routes[options.updatePlan.routeIndex] = imported.route;
                if (plan.selectedRouteIndex === options.updatePlan.routeIndex) {
                    plan.selectedRouteIndex = -1;
                }
            } else {
                plan.routes.push(imported.route);
                options.updatePlan.routeIndex = plan.routes.length-1;
            }

            if (imported.route.passagePlan) {
                options.passagePlan = imported.route.passagePlan;
            }

            imported.route.recalcNeeded = true;
            
            await VesselPlanningModel!.setPlan(plan.scheduleId, plan, false);
        }
        else
        {
            imported = await importFromFile(file);
        }

        this._fixupMaxSpeed(imported.route);

        //Add mandatory ECA/SECA waypoints
        const areasUtility: EcaSecaAreasUtilities = new EcaSecaAreasUtilities();
        try {
            imported.route = await areasUtility.applyToRoute(imported.route);
        } catch (error) {
            throw new Error(`ECA/SECA zone data could not be applied. Error details: ${(<Error>error).message}`);
        }

        imported.route.recalcNeeded = true;

        return this.postRoutingJobUsingRoute(imported.route, options);
    }

    async importShipModelFile(file: File): Promise<void> {
        return new Promise( (resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => {
                try {
                    const parsedFile = JSON.parse(<string>fr.result);
                    this.shipModel = {
                        shipProfile: parsedFile.ship_profile,
                        speedRanges: parsedFile.speed_ranges
                    };

                    if (!this.shipModel.shipProfile.ship_dynamics) {
                        // Model files from analytics seems to be missing ship_dynamics; add it here if necessary
                        this.shipModel.shipProfile.ship_dynamics = DefaultShipModel.shipProfile.ship_dynamics;
                    }

                    // Add these if missing (added in v4 of the routing API)
                    if (!this.shipModel.shipProfile.ship_measurements.cb) {
                        this.shipModel.shipProfile.ship_measurements.cb = DefaultShipModel.shipProfile.ship_measurements.cb;
                    }
                    if (this.shipModel.shipProfile.ship_type === undefined) {
                        this.shipModel.shipProfile.ship_type = DefaultShipModel.shipProfile.ship_type;
                    }

                    resolve();
                } catch (err) {
                    reject(err);
                }
            };
            fr.readAsText(file);
        })
    }

    async getNextWaypointIndex(maxAllowedDeviation: number, position: number[], routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): Promise<NextWaypointResult> {
        const payload : NextWaypointPayload = { maxAllowedDeviation, position, routeGeometry }
        const result = await RouteOptimizerService.getNextWaypointIndex(payload);
        
        return result;
    }

    getPositionAndHeadingInTime(timestamp: number, route: VoyageRoute) {
        if (!this._vesselMovementCache.has(route)) {
            this._createVesselMovementCacheGeoJson(<VoyageRoute>route);
        }

        const segment = this._vesselMovementCache.get(route)!.find(segment => timestamp >= segment.startDate && segment.endDate > timestamp)
        if (!segment) return null;

        const line = segment.line;

        // ####
        // Note on playback not being in sync i.e. the vessel-in-time icon is not starting exactly on the vessel icon.
        
        // The vessel-in-time icon will never be 100% in sync with actual vessel position because it is synced to hydra 
        // timestamps, and not the actual vessel position.

        // The above segments are based on Hard WPs and informational WPs. Timestamps on these are used to 
        // determine the position of the vessel-in-time icon - not the actual position of the vessel, because
        // this does not exist as a WP on the route. The position of vessel-in-time icon is determined based on
        // the time fraction between two waypoints. E.g., if 20% time has passed between two WPs, then vessel-in-time 
        // icon will be put on 20% along the particular segment (linestring). This might diverge from the actual, last
        // position of the vessel, if the vessel is not following the timestamps (calculated by Hydra).
        // ####
        const fraction = (timestamp - segment.startDate) / (segment.endDate - segment.startDate);
        const position = <[number,number]>line.getCoordinateAt(fraction);

        // Compute the heading by comparing the coordinate with another point on the line close to it
        let headingStart, headingEnd;
        if (fraction > 0.95)
        {
            headingStart = line.getCoordinateAt(fraction - 0.05);
            headingEnd = position;
        }
        else
        {
            headingStart = position;
            headingEnd = line.getCoordinateAt(fraction + 0.05);
        }
        const startLong = headingStart[0] * Math.PI / 180;
        const endLong = headingEnd[0] * Math.PI / 180;
        const startLat = headingStart[1] * Math.PI / 180;
        const endLat = headingEnd[1] * Math.PI / 180;
        const y = Math.sin(endLong-startLong) * Math.cos(endLat);
        const x = Math.cos(startLat)*Math.sin(endLat) - Math.sin(startLat)*Math.cos(endLat)*Math.cos(endLong-startLong);
        const heading = Math.atan2(y, x);

        const projectedPosition = <[number,number]>segment.projectedLine.getCoordinateAt(fraction);

        return {position, projectedPosition, heading};
    }

    getEtaAtPosition(projectedCoord: number[], route: VoyageRoute): number|null {
        if (!this._vesselMovementCache.has(route)) {
            this._createVesselMovementCacheGeoJson(route);
        }

        const segments = this._vesselMovementCache.get(route)!;
        let bestEta: number|null = null;
        let minDist = Infinity;

        for (const segment of segments) {
            if (segment.endDate <= Date.now()) continue;

            const closest = segment.projectedLine.getClosestPoint(projectedCoord);
            const dist = Math.hypot(closest[0] - projectedCoord[0], closest[1] - projectedCoord[1]);

            if (dist < minDist) {
                minDist = dist;
                const fraction = this._getFractionOnLine(segment.projectedLine, projectedCoord);
                bestEta = segment.startDate + fraction * (segment.endDate - segment.startDate);
            }
        }

        return bestEta;
    }

    private _getFractionOnLine(line: LineString, coord: number[]): number {
        const coords = line.getCoordinates();
        if (coords.length < 2) return 0;

        let totalLength = 0;
        const segLengths: number[] = [];
        for (let i = 0; i < coords.length - 1; i++) {
            segLengths.push(Math.hypot(coords[i + 1][0] - coords[i][0], coords[i + 1][1] - coords[i][1]));
            totalLength += segLengths[i];
        }
        if (totalLength === 0) return 0;

        let bestFraction = 0;
        let minDist = Infinity;
        let cumLength = 0;

        for (let i = 0; i < coords.length - 1; i++) {
            const segLen = segLengths[i];
            const dx = coords[i + 1][0] - coords[i][0];
            const dy = coords[i + 1][1] - coords[i][1];
            let t = 0;
            if (segLen > 0) {
                t = ((coord[0] - coords[i][0]) * dx + (coord[1] - coords[i][1]) * dy) / (segLen * segLen);
                t = Math.max(0, Math.min(1, t));
            }
            const dist = Math.hypot(coord[0] - (coords[i][0] + t * dx), coord[1] - (coords[i][1] + t * dy));
            if (dist < minDist) {
                minDist = dist;
                bestFraction = (cumLength + t * segLen) / totalLength;
            }
            cumLength += segLen;
        }
        return bestFraction;
    }

    private _routes: VoyageRoute[] = [];
    private _jobs: JobDetails[] = [];
    private _activePlan: string|null = null;
    private _uiRoutes: UIRouteMetadata[] = [];
    private _weatherLimits!: WeatherLimits;
    private _emitRoutesChanged: () => void;
    private _hydraJobCount: number = 0;
    private _backend: Backends;
    private _openEtaDeviationWarnings = new Set<string>();
    private _openWarnings = new Set<string>();
    private _recentWarningSignatures = new Map<string, number>();

    private _vesselMovementCache: WeakMap<VoyageRoute, VesselMovementCacheEntry[]> = new WeakMap();
    /**
     * @name _fixupMaxSpeed
     * @description Users might have entered 0 as max speed, which will cause an error
     * in Hydra calculation. So remove `speed_maximum` from route when it is 0
    */
     private _fixupMaxSpeed(route: VoyageRoute) {
        for (const feature of route.route_geometry) {
            if (feature.properties.type === "LineString") continue;

            if (feature.properties.speed_maximum === 0) {
                delete feature.properties.speed_maximum;
            }
        }
    }

    private _warningSignatureIsRecent(signature: string, maxAgeMs: number = 5000): boolean {
        const now = Date.now();

        for (const [key, timestamp] of this._recentWarningSignatures.entries()) {
            if (timestamp + maxAgeMs < now) {
                this._recentWarningSignatures.delete(key);
            }
        }

        const lastSeen = this._recentWarningSignatures.get(signature);
        if (lastSeen && lastSeen + maxAgeMs >= now) {
            return true;
        }

        this._recentWarningSignatures.set(signature, now);
        return false;
    }

    private _buildEtaWarningSignature(route: VoyageRoute, plan: VoyagePlan, scheduleId?: string, routeIndex?: number): string | null {
        const warnings = checkIfRequestedEtaWithin110PercentOfRequestedEta(route, plan);
        if (warnings instanceof Error || warnings.length === 0) {
            return null;
        }

        return JSON.stringify({
            type: "eta",
            scheduleId: scheduleId ?? "",
            warnings,
        });
    }

    private _buildCalculationWarningSignature(route: VoyageRoute, scheduleId?: string, routeIndex?: number): string | null {
        const warnings = route.route_info?.warnings;
        if (!Array.isArray(warnings) || warnings.length === 0) {
            return null;
        }

        return JSON.stringify({
            type: "calc",
            scheduleId: scheduleId ?? "",
            warnings,
        });
    }

    private _loadWeatherLimits(): void
    {
        const savedObject = localStorage.getItem("voyage-routing-model-weather-limits");
        if (!savedObject)
        {
            this._weatherLimits = {
                Waves: { avoid: null, warn: null },
                SwellHeight: { avoid: null, warn: null },
                HeadSwellHeight: { avoid: null, warn: null },
                HeadSwellPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                BeamSwellHeight: { avoid: null, warn: null },
                BeamSwellPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                FollowingSwellHeight: { avoid: null, warn: null },
                FollowingSwellPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                Wind10M: { avoid: null, warn: null },
                Wind50M: { avoid: null, warn: null },
                Visibility: { avoid: null, warn: null },
                Icing: { avoid: null, warn: null },
                Hurricanes: { avoid: null, warn: null },
                TropicalStorms: { avoid: null, warn: null },
                Depressions: { avoid: null, warn: null },
                WindGusts10M: { avoid: null, warn: null },
                WindGusts50M: { avoid: null, warn: null },
                RiskWindSpeed: { avoid: null, warn: null },
                RiskWaveHeight: { avoid: null, warn: null },
                HeadWind: { avoid: null, warn: null },
                BeamWind: { avoid: null, warn: null },
                FollowingWind: { avoid: null, warn: null },
                BeamWavesHeight: { avoid: null, warn: null },
                BeamWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                HeadWavesHeight: { avoid: null, warn: null },
                HeadWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                FollowingWavesHeight: { avoid: null, warn: null },
                FollowingWavesPeriod: { avoid: { max: null, min: null }, warn: { max: null, min: null } },
                IceConcentration: { avoid: null, warn: null },
                MaxRollAngle: { avoid: null, warn: null }
            };
            this.setWeatherLimits(this._weatherLimits);
        }
        else
        {
            this._weatherLimits = JSON.parse(savedObject);
        }
    }

    private async _postRoutingJobToRoutingAPI(inputRoute: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        if (!options.routeType) {
            throw new Error("Missing routeType in input to postRoutingJob");
        }

        options.shipModel = options.shipModel || this.shipModel;
        options.draft = options.draft || Math.max(options.draftFore!, options.draftAft!);

        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._uiRoutes[options.uiRouteIndex].status = "calculating";
                this._calculatingRoutes.push(options.uiRouteIndex);
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }

        try {
            const jobId = await RoutingService.postRoutingJob(inputRoute, options);

            this._addJob({ jobId: jobId!, calcOptions: options });
            this._emitRoutesChanged();
            return await this._getRoutingAPIResult(jobId!, options, inputRoute);
        } catch (err: any) {
            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan ) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = err.message;
                this._emitRoutesChanged();
            }

            throw err;
        }
    }

    private async _postRoutingJobToOptimiser(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {
        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._setRouteAndStatus(route, "calculating", options.uiRouteIndex);
                this._calculatingRoutes.push(options.uiRouteIndex);
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }
        
        let jobId: number;
        try {

            jobId = await RouteOptimizerService.postCalculationJob(route, {
                routeId: options.routeId,
                draftFore: options.draftFore || options.draft,
                draftAft: options.draftAft || options.draft,
                numberOfReefers: options.numberOfReefers ? options.numberOfReefers : 70,
                etd: options.etd,
                eta: options.eta,
                rollAngle: options.rollAngle,
                weatherLimits: options.weatherLimits,
                forecastVersion: options.forecastVersion,
                planScheduleId: options.planScheduleId,
                gmt: options.gmt,
                weatherRoutingInfo: options.weatherRoutingInfo,
                solidGm: options.solidGm,
                whaleZonesApplied: options.whaleZonesApplied,
                disableWhaleZoneSpeedRestrictions: options.disableWhaleZoneSpeedRestrictions,
            });
            
        } catch (error) {
            //if vessel is offline and eta/etd are updated triggering recalc then approved plan must stay approved
            if (isVesselOnCloud && !isOnline() && options.recalcParametersUpdate) {
                const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
                if (plan && options.updatePlan && options.updatePlan.autoApprove) {
                    plan.status = options.updatePlan.autoApprove ? "approved" : "planning";
                    plan.selectedRouteIndex = options.updatePlan.selectedRouteIndex ?? plan.routes.length - 1;

                    await VesselPlanningModel!.setPlan(plan.scheduleId, plan, false);
                }

                return {} as VoyageRoute;
            }

            throw new Error();
        }

        this._addJob({ jobId: jobId!, calcOptions: options });

        const result = await RouteOptimizerService.waitForJob(jobId!, options);
        
        this._removeJob(jobId!);

        if (result.status !== "complete")
        {
            if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                this._uiRoutes[options.uiRouteIndex].status = "failed";
                this._uiRoutes[options.uiRouteIndex].error = result.statusDetails || null;
                this._emitRoutesChanged();
            }

            if (result.errorDetails && result.errorDetails.hydraRequestID)
            {
                throw new RouteOptimiserCalcError(result.errorDetails.hydraRequestID, result.errorDetails.hydraErrorBody, "Route calculation failed");
            }
            else
            {
                throw new Error(result.statusDetails);
            }
        }

        if(options.shouldRecalculate) {
                options.etd = VesselPositionModel!.position ? new Date(VesselPositionModel!.position.timestamp).getTime() : new Date().getTime();;
                result.route = await markPassedAndAddCurrentPosition(result.route!, true, true);
                options.shouldRecalculate = false;
                return await this._postRoutingJobToOptimiser(result.route!, options);
        }

        // Restore RTZ-imported user values overwritten by the optimiser.
        // Any waypoint property stored in `userInput` (written by RTZConverter._setImportedUserValue)
        // is treated as a manual user entry and must survive the optimisation response.
        const inputWaypointsWithUserInput = route.route_geometry.filter(
            f => f.geometry.type === 'Point' && !!(f.properties as any).userInput
        ) as VoyageRoutePointFeature[];

        if (inputWaypointsWithUserInput.length > 0) {
            for (const resultFeature of result.route!.route_geometry) {
                if (resultFeature.geometry.type !== 'Point') continue;
                const resultWP = resultFeature as VoyageRoutePointFeature;
                const match = inputWaypointsWithUserInput.find(
                    wp => wp.geometry.coordinates[0] === resultWP.geometry.coordinates[0]
                       && wp.geometry.coordinates[1] === resultWP.geometry.coordinates[1]
                );
                if (!match) continue;
                const userInput = (match.properties as any).userInput as Record<string, number>;
                for (const [key, value] of Object.entries(userInput)) {
                    (resultWP.properties as any)[key] = value;
                }
                // Carry userInput forward so future re-optimisations also respect these values
                (resultWP.properties as any).userInput = {
                    ...((resultWP.properties as any).userInput || {}),
                    ...userInput
                };
            }
        }

        //Update zones flags
        if (result.route!.route_info) {
            result.route!.route_info.whaleZonesApplied = options.whaleZonesApplied;
            result.route!.route_info.disableWhaleZoneSpeedRestrictions = options.disableWhaleZoneSpeedRestrictions;
        }

        // The optimiser returns a fresh route_info, so a route the user already reviewed would fall
        // straight back into "needs review" and keep Passage Plan locked.
        await this._carryRouteWarningsReview(result.route!, options);

        if (options.passagePlan) {
            result.route!.passagePlan = options.passagePlan;
        }

        if (options?.weatherRoutingInfo) {
            result.route!.weatherRoutingInfo = options?.weatherRoutingInfo;
        }

        // Add created time, isPassedUpdate and uiType if needed
        result.route!.creationTime = new Date().toISOString();
        result.route!.recalcNeeded = false;
        if (route.isPassedUpdate)
        {
            result.route!.isPassedUpdate = route.isPassedUpdate;
        }
        if (route.name)
        {
            result.route!.name = route.name;
        }
        if(route.bestPracticeRouteId) {
            result.route!.bestPracticeRouteId = route.bestPracticeRouteId;
        }
        if (route.uiType)
        {
            result.route!.uiType = route.uiType;
        }
        if (route.routeCreatedBy) {
            result.route!.routeCreatedBy = route.routeCreatedBy;
        }

        if (route.routeIdParent) {
            result.route!.routeIdParent = route.routeIdParent;
        }

        if (route?.bprAlignment?.isAligned) {
            result.route!.bprAlignment = route.bprAlignment;
        }

        if (route?.bprAdoption) {
            result.route!.bprAdoption = route.bprAdoption;
        }

        if (options.routeId)
            {
                result.route!.routeId = options.routeId;
            }

        if (options.updatePlan)
        {
            this._updatePlanWithRoute(result.route!, options);
        }
        else if (options.addRouteToModel)
        {
            this.addRoute(result.route!);
        }

        this.dispatchEvent(new CustomEvent<VoyageRoute>(RoutingModelEvents.calculationCompleted));
        return result.route!;
        
    }
    private _calculatingRoutes: Number[] = [];

    private async _postRoutingJobToHydra(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>
    {    
        if (options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
            if (options.uiRouteIndex >= 0 && options.uiRouteIndex < this._uiRoutes.length) {
                this._setRouteAndStatus(route, "calculating", options.uiRouteIndex);
                this._calculatingRoutes.push(options.uiRouteIndex);
                this._emitRoutesChanged();
            } else {
                console.warn("uiRouteIndex out of bounds!");
                delete options.uiRouteIndex;
            }
        }

        const jobId = this._hydraJobCount++;
        this._addJob({ jobId: jobId, calcOptions: options });
        let result: VoyageRoute;

        try
        {
            result = await HydraRoutingService.postCalculationJob(route, {
                routeId: options.routeId,
                draftFore: options.draftFore || options.draft,
                draftAft: options.draftAft || options.draft,
                gmt: options.gmt,
                reeferConsumption: options.numberOfReefers ? options.numberOfReefers*3.5 : 250,
                etd: options.etd,
                eta: options.eta,
                forecastVersion: options.forecastVersion,
                imo: options.imo,
                maxSegmentDistance: options.maxSegmentDistance
            });
            
            const routeWeatherWorker = new RouteWeatherWorker(result, this._weatherLimits, false);
            await routeWeatherWorker.processRoute();
        }
        catch (err)
        {
            this._removeJob(jobId);
            
            throw err;
        }

        this._removeJob(jobId);

        //Update zones flags
        if (result.route_info) {
            result.route_info.whaleZonesApplied = options.whaleZonesApplied;
            result.route_info.disableWhaleZoneSpeedRestrictions = options.disableWhaleZoneSpeedRestrictions;
        }

        if (options.passagePlan) {
            result.passagePlan = options.passagePlan;
        }

        result.creationTime = new Date().toISOString();
        if (route.isPassedUpdate)
        {
            result.isPassedUpdate = route.isPassedUpdate;
        }
        if (route.name)
        {
            result.name = route.name;
        }
        if(route.routeCreatedBy) {
            result.routeCreatedBy = route.routeCreatedBy;
        }
        if (options.uiRouteIndex !== undefined)
        {
            result.uiType = route.uiType;
        }
        if (options.routeId )
            {
                result.routeId = options.routeId;
            }

        if (options.updatePlan)
        {
            this._updatePlanWithRoute(result, options);
        }
        else if (options.addRouteToModel)
        {
            this.addRoute(result);
        }

        return result;
    }

    private _onPlanChanged(evt: PlanChangedEvent): void
    {
        if (evt.detail.scheduleId === this._activePlan)
        {
            this._updateRoutesFromPlan();
        }
    }

    private async _updateRoutesFromPlan(): Promise<void>
    {
        this._clearUIRoutes();
        if (this._activePlan)
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(this._activePlan);
            if (plan)
            {
                for (let i = 0; i < plan.routes.length; i++)
                {
                    const route = plan.routes[i];
                    if (route.route_info && route.route_geometry)
                    {  
                        this._setRouteAndStatus(route, "idle", i);
                        // if it is there in calculating routes, then remove from there
                        const index = this._calculatingRoutes.indexOf(i);
                        if (index > -1) {
                            this._calculatingRoutes.splice(index, 1);
                        }
                    } else if (this._calculatingRoutes.includes(i)) {
                        this._setRouteAndStatus(route, "calculating", i);
                    } else {
                        this._setRouteAndStatus(route, "failed", i);
                        const index = this._calculatingRoutes.indexOf(i);
                        if (index > -1) {
                            this._calculatingRoutes.splice(index, 1);
                        }
                    }
                    
                    await this._detectZones(route!, i);
                    if (i < this._uiRoutes.length) {
                        this._uiRoutes[i].selected = (i === plan.selectedRouteIndex);
                    }
                }
            }
        }

        // Check if there are any (relevant) ongoing calculations
        for (const job of this._jobs)
        {
            if (job.calcOptions && job.calcOptions.uiRouteIndex !== undefined &&
                job.calcOptions.updatePlan && job.calcOptions.planScheduleId === this._activePlan)
            {
                this._uiRoutes[job.calcOptions.uiRouteIndex].status = "calculating";
            }
        }
    }

    private _clearUIRoutes(): void
    {
        for (let i = 0; i < this._uiRoutes.length; i++)
        {
            this._setRouteAndStatus(null, "idle", i);
        }
    }

    private _setRouteAndStatus(route: VoyageRoute|null, status: RouteCalcStatus, uiRouteIndex: number): void
    {
        if (uiRouteIndex >= this._uiRoutes.length || uiRouteIndex < 0) {
            console.warn("uiRouteIndex out of range!");
            return;
        }

        if (this._uiRoutes[uiRouteIndex].routeIndex >= 0)
        {
            this.deleteRoute(this._uiRoutes[uiRouteIndex].routeIndex);
        }
        this._uiRoutes[uiRouteIndex].status = status;

        if (status !== "failed") 
        {
            this._uiRoutes[uiRouteIndex].error = null;
        }

        this._uiRoutes[uiRouteIndex].routeIndex = route ? this.addRoute(route) : -1;
        // check if it is a BPR route and append the uiType with appropriate value
        if (route) {
            route.uiType = route.routeCreatedBy === 'system' ? 'BPR' : route.routeCreatedBy === 'WR' ? 'WeatherRouting' : `UserRoute${uiRouteIndex}`;
        }
    }

    private async _updatePlanWithRoute(route: VoyageRoute, options: RoutingJobOptions): Promise<void>
    {
        const plan = await VesselPlanningModel!.getCurrentPlan(options.planScheduleId!);
        if (!plan)
        {
            console.warn("Could not find a plan for id ", options.planScheduleId);
            return;
        }

        if (route.routeId)
        {
            // Guard against saving more than one route with same uiType
            const currentIndex = plan.routes.findIndex( r => r.routeId === route.routeId );
            if (currentIndex !== -1)
            {
                options.updatePlan!.routeIndex = currentIndex;
            }
        }

        if (options.updatePlan!.routeIndex >= 0)
        {
            plan.routes[options.updatePlan!.routeIndex] = route;
        }
        else
        {
            plan.routes.push(route);
        }

        if (options.updatePlan!.selectRoute)
        {
            plan.selectedRouteIndex = options.updatePlan!.routeIndex >= 0 ? options.updatePlan!.routeIndex : plan.routes.length - 1;
        }
        
        if(options.updatePlanEta){
            if(options.eta)
                plan.eta = options.eta
            if(options.arrivalPilotEta)
                plan.arrivalPilotEta = options.arrivalPilotEta
        }

        let hasArrivalPilotEtaChanged = false;

        if(options?.oldPlanArrivalPilotEta && plan.arrivalPilotEta) {
            hasArrivalPilotEtaChanged = hasPilotEtaChanged(options?.oldPlanArrivalPilotEta, plan.arrivalPilotEta);
        }
        
        await VesselPlanningModel!.setPlan(options.planScheduleId!, plan, false);

        // Check if there is any active job running
        // If there is no job running and the plan is not approved, then approve the plan
        const jobCount = this._jobs.filter(job => job.calcOptions && job.calcOptions.planScheduleId === options.planScheduleId).length;

        if (plan.status !== "approved" && jobCount === 0) {
            if (options.updatePlan?.autoApprove && plan.selectedRouteIndex >= 0) {
                VesselPlanningModel!.approvePlan(options.planScheduleId!, hasArrivalPilotEtaChanged);
            }
        }
    }

    private _createUIRoutes(count: number): void {
        for (let i = 0; i < count; i++) {
            this._uiRoutes.push({
                routeId: "",
                name: '',
                status: "idle",
                routeIndex: -1,
                error: null,
                selected: false,
                crossingWhaleZones: false
            });
        }
    }

    private _checkForURLJobs() {
        // Pull job_id off address and look it up
        const jobId = UiStateModel!.getValue('job_id')
        if (jobId) {
            UiStateModel!.setValue('job_id', null);
            this._addJob({ jobId });
            this._emitRoutesChanged();
            this._getRoutingAPIResult(jobId);
        }
    }

    private async _getRoutingAPIResult(jobId: string, options?: RoutingJobOptions, inputRoute?: VoyageRoute): Promise<VoyageRoute>
    {
        const result = await RoutingService.getResultForJob(jobId!, inputRoute);

        if (result && result.status === "Done" && options)
        {
            // Add the used calculation parameters (for the dynamic parts)
            // forecast_versions seems to be missing from the reply sometimes (sigh...) - default to the latest forecast if it is not there
            let forecast_versions = [WeatherModel!.latestForecastVersion];
            if (result.request.route_parameters.weather && result.request.route_parameters.weather.forecast_versions) {
                forecast_versions = result.request.route_parameters.weather.forecast_versions;
            }

            result.status_info.route_info.calculationInput = {
                forecast_versions: forecast_versions,
                draftAft: options.draftAft || options.draft,
                draftFore: options.draftFore || options.draft,
                etd: result.request.route_parameters.etd,
                eta: result.request.route_parameters.eta,
                weatherLimits: options.weatherLimits
            };

            if (options.routeType !== "ShortestDistance")
            {
                try {
                    const routeWeatherWorker = new RouteWeatherWorker(result.status_info, this._weatherLimits, false);
                    await routeWeatherWorker.processRoute();
                } catch (err: any) {
                    result.status = "failed";
                    result.status_info.message = err.message;
                }
            }

            if (options.passagePlan) {
                result.passagePlan = options.passagePlan;
            }
        }

        // Remove from list again
        this._removeJob(jobId);

        if (!result || result.status !== "Done") {
            const error = result && result.status_info && result.status_info.message ? result.status_info.message : "Unknown error occurred";

            if (options && options.uiRouteIndex !== undefined && options.updatePlan && options.planScheduleId == this._activePlan) {
                const uiRoute = this._uiRoutes[options.uiRouteIndex];
                uiRoute.status = "failed";
                uiRoute.error = error;
            }

            this._emitRoutesChanged();
            throw new Error(error);
        }

        const route = result.status_info;

        // Add created time and isPassedUpdate
        route.creationTime = new Date().toISOString();
        if (inputRoute && inputRoute.isPassedUpdate)
        {
            route.isPassedUpdate = inputRoute.isPassedUpdate;
        }

        if (options) {
            // Add uiType if needed
            if (options.uiRouteIndex !== undefined)
            {
                route.uiType = inputRoute?.uiType || `UserRoute${options.uiRouteIndex}`;
            }
            if (options.routeId)
                {
                    route.routeId = options.routeId
                }

            if (options.updatePlan)
            {
                this._updatePlanWithRoute(route, options);
            }
            else if (options.addRouteToModel)
            {
                this.addRoute(route);
            }

        } else {
            this.addRoute(route);
        }

        return route;
    }

    private _addJob(job: JobDetails)
    {
        this._jobs.push(job);
        this.dispatchEvent(new Event('jobs-changed'));
    }

    private _removeJob(jobId: string|number)
    {
        this._jobs = this._jobs.filter( jobDetails => jobDetails.jobId !== jobId );
        this.dispatchEvent(new Event('jobs-changed'));
    }

    private _findGeoJsonPoint(features: (VoyageRoutePointFeature|VoyageRouteLineFeature)[],
                              coords: [number, number], indexHint: number): VoyageRoutePointFeature|null
    {
        // Try the indexHint first, then search through the entire array
        if (features[indexHint].geometry.type === "Point" &&
            features[indexHint].geometry.coordinates[0] === coords[0] && features[indexHint].geometry.coordinates[1] === coords[1])
        {
            return <VoyageRoutePointFeature>features[indexHint];
        }

        for (const feature of features)
        {
            if (feature.geometry.type === "Point" &&
                feature.geometry.coordinates[0] === coords[0] && feature.geometry.coordinates[1] === coords[1])
            {
                return <VoyageRoutePointFeature>feature;
            }
        }

        console.warn("Could not find a Point with coordinates", coords);
        return null;
    }

    private _createVesselMovementCacheGeoJson(route: VoyageRoute)
    {
        const cache: VesselMovementCacheEntry[] = [];

        for (let i = 0; i < route.route_geometry.length; i++)
        {
            const feature = route.route_geometry[i];

            if (feature.geometry.type === "LineString")
            {
                // Historical WPs are not sent to Hydra and thus not re-calculated. This means
                // timestamps on historical WPs will never change.
                // If vessel changes ETA during voyage, the historical timestamps can be a lot
                // behind vessel, and thus vessel-in-time icon can be a lot befind vessel icon.
                // To fix this, skip isPassed linestrings, so vessel-in-time icon is following
                // updated, future timestamps.
                if (i > 0 && route.route_geometry[i-1].properties.isPassed) {
                    continue;
                }

                const endCoords = feature.geometry.coordinates[1];
                const endPoint = this._findGeoJsonPoint(route.route_geometry, endCoords, i+1);

                if (feature.properties.informationalWaypoints)
                {
                    for (let i = 0; i < feature.properties.informationalWaypoints.length; i++)
                    {
                        const wp = feature.properties.informationalWaypoints[i];
                        const nextWP = i < feature.properties.informationalWaypoints.length - 1 ? feature.properties.informationalWaypoints[i+1] : endPoint;

                        const startDate = wp.properties.timestamp!;
                        const endDate = nextWP.properties.timestamp!;

                        const line = getLineStringForFractionPositions({
                            type: "Feature",
                            geometry: { type: "LineString", coordinates: [wp.geometry.coordinates, nextWP.geometry.coordinates] },
                            properties: { leg_type: feature.properties.leg_type }
                        });
                            
                        const projectedLine = <LineString>line.clone();
                        projectedLine.transform('EPSG:4326', _PROJECTION);

                        cache.push({
                            startDate: new Date(startDate).getTime(),
                            endDate: new Date(endDate).getTime(),
                            line: line,
                            projectedLine
                        });
                    }
                }
                else
                {
                    const startCoords = feature.geometry.coordinates[0];
                    const startPoint = this._findGeoJsonPoint(route.route_geometry, startCoords, i-1);
                    const startDate = startPoint!.properties.timestamp! || route.route_info!.etd; // Start Point seems to be missing timestamp
                    const endDate = endPoint!.properties.timestamp!;
    
                    const line = getLineStringForFractionPositions(<VoyageRouteLineFeature>feature);
    
                    const projectedLine = <LineString>line.clone();
                    projectedLine.transform('EPSG:4326', _PROJECTION);
    
                    const result = {
                        startDate: new Date(startDate).getTime(),
                        endDate: new Date(endDate).getTime(),
                        line: line,
                        projectedLine
                    };
    
                    cache.push(result);    
                }
            }
        }

        this._vesselMovementCache.set(route, cache);
    }

    // Check if the route is crossing any zones
    private async _detectZones(route: VoyageRoute, uiRouteIndex: number) {
        if (!this._uiRoutes[uiRouteIndex])
            return;

        try {
            this._uiRoutes[uiRouteIndex].crossingWhaleZones = await (new WhaleZonesUtilities().routeCrossingZones(route.route_geometry));
        } catch (err) {
            console.warn("Failed to detect whale zone crossings:", err);
        }
    }

    async onEditBprRoute(plan: VoyagePlan, scheduleId: string, route: VoyageRoute, BprRouteId: string) {
        route.passagePlan = {
            description: "",
            reminders: "",
            notEcdisCompliant: false,
            encCellsUsed: "",
            booksAndTables: "",
            chartsAndLightsNotes: "",
            sailingDirections: "",
            listOfRadioSignals: "",
            listOfLights: "",
            gpsEcdisRadarMap: "",
            paperCharts: "",
            pubCorrectedToWeek: "",
            tideTables: "",
            publicationsNotes: "",
            mastersOrders: "",
            captain: "",
            navigationalOfficer: "",
            planDate: null,
            safetyMessages: {
                navareaRegions: "",
                navtexStations: ""
            },
            weatherInfo: {
                navareaRegions: "",
                navtexStations: "",
                info: ""
            },
            equipmentStatus: {
                operationalCondition: "",
                failures: ""
            },
            weatherRouting: {
                required: false,            
                acknowledged: false,     
                pilotPointAcknowledged: false 
            }
        }

        // adding routeIdParent to the route
        route.routeIdParent = BprRouteId;
        route.recalcNeeded = true;
        
        addPrefillValuesToPassagePlan(route.passagePlan)
        route.routeId = UUID();
        route.creationTime = new Date().toISOString();

        const isAligned = await _checkRouteAlignment(route, plan);
        const routeNames = getNamesOfRoutesFromPlan(plan);
        const newRouteName = getEdittedStarRouteName(routeNames, isAligned ? 'Star Route Within Limits' : 'Star Route Outside Limits');
        route.name = newRouteName;
        plan.routes.push(route);
        await VesselPlanningModel!.setPlan(scheduleId, plan, false);
        await this._syncPilotETAs(plan, route);
        const options: RoutingJobOptions = {
            routeId: route.routeId,
            draftFore: plan.foreDraft,
            draftAft: plan.aftDraft,
            gmt: plan.gmt !== null ? plan.gmt : undefined,
            etd: plan.etd,
            eta: plan.eta,
            rollAngle: plan.rollAngle !== null ? plan.rollAngle : undefined,
            numberOfReefers: plan.numberOfReefers,
            routeType: "WeatherOptimized",
            planScheduleId: scheduleId,
            whaleZonesApplied: false,
            // The route has been edited, so any earlier opt-out is dropped and analytics
            // applies the whale zone speed restrictions again.
            disableWhaleZoneSpeedRestrictions: false,
            passagePlan: route.passagePlan,
            updatePlan: {
                routeIndex: plan.routes.findIndex(r => r.routeId === route.routeId),
            },
            uiRouteIndex: plan.routes.length-1,
            weatherRoutingInfo: route.weatherRoutingInfo,
            solidGm: plan.solidGm !== null ? plan.solidGm : undefined,
        };
        // Trigger calculation, but don't wait for the result
        this.postRoutingJobUsingRoute(route, options);
    }
    
    async saveRoute(route: VoyageRoute ,uiRouteIndex: number, scheduleId: string, routeId: string) {
        const plan = await VesselPlanningModel!.getCurrentPlan(scheduleId);
        if (!plan) {
            console.warn(`No plan found for ${scheduleId}, changes will be lost!`);
            return;
        }
        
        const executingPlan = await VesselPlanningModel!.getExecutingPlan();
        const isExecutingPlan = executingPlan && executingPlan.scheduleId === plan.scheduleId;

        // Overwrite any route already present with the given uiRouteIndex
        let planIndex = plan.routes.findIndex(route => route.routeId === routeId);

        const bprRoute = plan.routes.find(r => r.routeId === routeId && r.routeCreatedBy === 'system');
        const wrRoute = plan.routes.find(r => r.routeId === routeId && r.routeCreatedBy === 'WR');

        // The route warnings review (const_pow_endpoint_v2) is acknowledged on the route object the
        // editor hands over, and what happens next depends on whether the user amended the route
        // while reviewing it - decided by the very comparison that decides whether an approved plan
        // survives the save (see autoApprove below).
        if (await this._saveRouteWarningsReviewOnly(route, plan, planIndex, scheduleId)) {
            return;
        }

        let isBothRouteSame = true;
        if(bprRoute) {
            // comapre route geometry with bpr route geometry
            isBothRouteSame  = compareRoutes(bprRoute, route);
        }

        if(!isBothRouteSame){
            route.weatherRoutingInfo = route.weatherRoutingInfo ?? bprRoute!.weatherRoutingInfo;
            this.onEditBprRoute(plan, scheduleId, route, bprRoute!.bestPracticeRouteId!);
        } else {
        // Save route now (so we don't lose it if the calculation fails for some reason)
        const wasSelected = planIndex >= 0 && plan.selectedRouteIndex === planIndex;
        let oldRoute: VoyageRoute|null = null;
        route.uiType = bprRoute ? bprRoute.uiType : `UserRoute${uiRouteIndex}`;
        route.routeCreatedBy = bprRoute ? bprRoute.routeCreatedBy : route.routeCreatedBy;
        // Baseline name first, so the alignment check below (is-adopted) can override it with
        // "Within Limits"/"Outside Limits" instead of being immediately clobbered by it.
        route.name = bprRoute ? bprRoute.name : route.name;
        if(route.name?.includes('Star Route')) {
            const routeNames = getNamesOfRoutesFromPlan(plan);
            const isAligned = await _checkRouteAlignment(route, plan);
            route.name = getEdittedStarRouteName(routeNames, isAligned ? 'Star Route Within Limits' : 'Star Route Outside Limits');
        }
        route.recalcNeeded = true;

        if (planIndex >= 0) {
            oldRoute = plan.routes[planIndex] || null;
            route.passagePlan = plan.routes[planIndex].passagePlan!;
            route.weatherRoutingInfo = route.weatherRoutingInfo ?? plan.routes[planIndex].weatherRoutingInfo;
            plan.routes[planIndex] = route;
            if (wasSelected) {
                plan.selectedRouteIndex = -1;
            }
        } else {
                route.passagePlan = {
                    description: "",
                    reminders: "",
                    notEcdisCompliant: false,
                    encCellsUsed: "",
                    booksAndTables: "",
                    chartsAndLightsNotes: "",
                    sailingDirections: "",
                    listOfRadioSignals: "",
                    listOfLights: "",
                    gpsEcdisRadarMap: "",
                    paperCharts: "",
                    pubCorrectedToWeek: "",
                    tideTables: "",
                    publicationsNotes: "",
                    mastersOrders: "",
                    captain: "",
                    navigationalOfficer: "",
                    planDate: null,
                    safetyMessages: {
                        navareaRegions: "",
                        navtexStations: ""
                    },
                    weatherInfo: {
                        navareaRegions: "",
                        navtexStations: "",
                        info: ""
                    },
                    equipmentStatus: {
                        operationalCondition: "",
                        failures: ""
                    },
                    weatherRouting: {
                        required: false,            
                        acknowledged: false,     
                        pilotPointAcknowledged: false 
                    }
                }
                
                addPrefillValuesToPassagePlan(route.passagePlan)

                    plan.routes.push(route);
                    planIndex = plan.routes.length-1;
                }

                await this._syncPilotETAs(plan, route);

                // update sailing directions when the route has changed
                if (oldRoute) {
                    const routeChanged = VesselPlanningModel!.passagePlanRouteChanged(oldRoute.route_geometry, route.route_geometry);
                    
                    if (routeChanged) {
                        let weatherRouting = plan.routes[planIndex].passagePlan?.weatherRouting;
                        if (weatherRouting) {
                            weatherRouting = {required: false, acknowledged: false,pilotPointAcknowledged: false }
                        }
                        
                        plan.passagePlan.sailingDirections = VesselEnpBookAndChapterModel!.getCrossedEnpChaptersAndBooks(route.route_geometry);
                    }

                    if (oldRoute.routeCreatedBy !== 'bridge') {
                    let hardWPsHasChanged = false;
                    const oldGeometry = [];
                    const newGeometry = [];
                    for (const feature of oldRoute.route_geometry) {
                        if (feature.properties.type !== 'Hard') {
                            continue;
                        }
                        oldGeometry.push(feature);
                    }
                    for (const feature of route.route_geometry) {
                        if (feature.properties.type !== 'Hard') {
                            continue;
                        }
                        newGeometry.push(feature);
                    }
                    if (oldGeometry.length !== newGeometry.length) {
                        hardWPsHasChanged = true;
                    }
                    for (let i = 0; i < oldGeometry.length; i++) {
                        if (oldGeometry[i].geometry.coordinates[0] !== newGeometry[i].geometry.coordinates[0]
                            || oldGeometry[i].geometry.coordinates[1] !== newGeometry[i].geometry.coordinates[1]
                        ) {
                            hardWPsHasChanged = true;
                            break;
                        }
                    }
                    if (hardWPsHasChanged) {
                        // If a route has received major changes, set it to 'bridge'
                        console.log("Major change by user - changing routeCreatedBy to 'bridge'")
                        route.routeCreatedBy =  bprRoute ? bprRoute.routeCreatedBy : wrRoute ? wrRoute.routeCreatedBy : "bridge";
                    }
                }
            }

            // Keep approval if passage plan is not affected (ie. only navigational waypoints have been changed)
            let autoApprove = false;
            if (plan.status === "approved" && wasSelected && oldRoute)
            {
                autoApprove = VesselPlanningModel!.passagePlanRouteChanged(oldRoute.route_geometry, route.route_geometry) === false;
            }
            if (plan.status === "approved" && !autoApprove)
            {
                const popupText= `Earlier approved plan have been detected as modified; to ensure correct plan is handled; please approve and send the updated plan.`
                VesselPlanningModel!.approvalReminder(popupText);
            }
            await VesselPlanningModel!.setPlan(scheduleId, plan, false);

        //if the plan is underway, for all routes it should take current time for recalculation
        const isExecutingPlanUnderway = isExecutingPlan && plan.etd < new Date().getTime()
        
        const options: RoutingJobOptions = {
            routeId: route.routeId,
            draftFore: plan.foreDraft,
            draftAft: plan.aftDraft,
            gmt: plan.gmt !== null ? plan.gmt : undefined,
            etd: isExecutingPlanUnderway ? Date.now() : plan.etd,
            eta: plan.eta,
            rollAngle: plan.rollAngle !== null ? plan.rollAngle : undefined,
            numberOfReefers: plan.numberOfReefers,
            routeType: "WeatherOptimized",
            uiRouteIndex: uiRouteIndex,
            planScheduleId: scheduleId,
            updatePlan: {
                routeIndex: planIndex,
                selectRoute: wasSelected,
                autoApprove: autoApprove
            },
            whaleZonesApplied: false,
            // The route has been edited, so any earlier opt-out is dropped and analytics
            // applies the whale zone speed restrictions again.
            disableWhaleZoneSpeedRestrictions: false,
            passagePlan: route.passagePlan,
            weatherRoutingInfo: route.weatherRoutingInfo,
            solidGm: plan.solidGm !== null ? plan.solidGm : undefined,
        };
        // If plan is underway, then mark waypoints which are passed as isPassed using the vessel current position 
        if(plan.etd < new Date().getTime()) {
             await markPassedAndAddCurrentPosition(route);
        }
        // Trigger calculation, but don't wait for the result
        this.postRoutingJobUsingRoute(route, options);
        }
    }

    /**
     * ==================================================================================
     * ROUTE WARNINGS REVIEW ON SAVE (const_pow_endpoint_v2 only)
     * ==================================================================================
     *
     * The route editor stamps route_info.routeWarningsReviewed = true on the route it hands to
     * saveRoute when the user confirmed the precautionary zone warnings with "Mark as reviewed"
     * and left through "Save & Exit". Two things can have happened by then:
     *
     * 1. The user amended the route. The relevant test is the same one that decides whether an
     *    approved plan stays approved (passagePlanRouteChanged: hard waypoints and leg types), so
     *    a change big enough to unapprove the plan is also a change big enough to invalidate the
     *    review - the route no longer necessarily crosses the zones that were reviewed. The
     *    acknowledgement is dropped here and the save continues as usual; the recalculation then
     *    re-establishes it only if the crossings come back identical (see
     *    _postRoutingJobToOptimiser).
     *
     * 2. The user only reviewed. There is nothing to recalculate, so the acknowledgement is
     *    written onto the stored route and the plan is persisted on its own. Saving the route the
     *    editor rebuilt would be wrong here: its legs carry none of the optimiser's results (see
     *    RouteEditModel.generateRouteGeometry), which is exactly why an ordinary save has to
     *    recalculate. Keeping the calculated route untouched avoids both the recalculation and the
     *    round trip through "calculating" that would put the route back in front of the user.
     *
     * Any other edit - an ETA, a speed, a comment - leaves the acknowledgement alone (the zones are
     * unchanged) but still needs the recalculation, so it takes the normal path too.
     *
     * @returns true when the acknowledgement was all there was to save and saveRoute is done.
     */
    private async _saveRouteWarningsReviewOnly(route: VoyageRoute, plan: VoyagePlan, planIndex: number,
                                               scheduleId: string): Promise<boolean>
    {
        if (route.route_info?.routeWarningsReviewed !== true) {
            return false;
        }

        const storedRoute = planIndex >= 0 ? plan.routes[planIndex] : null;
        const zoneRelevantGeometryChanged = !storedRoute
            || VesselPlanningModel!.passagePlanRouteChanged(storedRoute.route_geometry, route.route_geometry);

        if (zoneRelevantGeometryChanged) {
            route.route_info!.routeWarningsReviewed = false;
            return false;
        }

        if (hasRouteEdits(storedRoute!.route_geometry, route.route_geometry)) {
            // The zones are the same ones the user reviewed, so the acknowledgement stands, but the
            // edits still have to be calculated. The optimiser carries the flag across.
            return false;
        }

        if (!storedRoute!.route_info) {
            return false;
        }

        // Replaced rather than written into: a plan handed out by VesselPlanningModel is sealed on
        // the way out, and the acknowledgement may be a property this route_info does not carry yet.
        storedRoute!.route_info = { ...storedRoute!.route_info, routeWarningsReviewed: true };
        await VesselPlanningModel!.setPlan(scheduleId, plan, false);
        this._emitRoutesChanged();
        return true;
    }

    /**
     * ==================================================================================
     * ROUTE WARNINGS REVIEW ACROSS A RE-CALCULATION
     * ==================================================================================
     *
     * A route whose precautionary zone warnings the user has already acknowledged must not drop
     * back into "needs review" just because it was re-optimised - that would re-lock "Select route"
     * and "Passage Plan" behind a review of zones the user has already seen.
     *
     * Both the acknowledgement and the geometry it was given for are read off the route as the plan
     * holds it, which is the state the user reviewed. The acknowledgement is kept only while the
     * calculated route still crosses the same zone points; a zone gained, lost, or crossed at a
     * different waypoint is a warning the user has not seen, so the flag is dropped.
     *
     * Reading the plan rather than the input route matters for a double calculation: that recursion
     * re-enters the optimiser with the optimiser's own output, which carries neither the
     * acknowledgement nor the reviewed geometry. The plan is untouched until _updatePlanWithRoute
     * further down, and the first pass returns before reaching it, so the plan still holds the
     * reviewed route on every pass.
     */
    private async _carryRouteWarningsReview(calculatedRoute: VoyageRoute, options: RoutingJobOptions): Promise<void>
    {
        if (!calculatedRoute.route_info) {
            return;
        }

        const plan = options.planScheduleId
            ? await VesselPlanningModel!.getCurrentPlan(options.planScheduleId)
            : null;
        const reviewedRoute = plan?.routes?.find(r => r.routeId === options.routeId);

        calculatedRoute.route_info.routeWarningsReviewed =
            reviewedRoute?.route_info?.routeWarningsReviewed === true
            && getZoneCrossingSignature(reviewedRoute.route_geometry as any[])
                === getZoneCrossingSignature(calculatedRoute.route_geometry as any[]);
    }

    private async _syncPilotETAs(plan: VoyagePlan, updatedRoute: VoyageRoute)
    {
        const updatedPilotPoints = updatedRoute.route_geometry.filter( feature => feature.geometry.type === "Point" && feature.properties.pilotPoint );
        if (updatedPilotPoints.length < 1) {
            return;
        }
        const newDeparturePilot = (<VoyageRoutePointFeature[]>updatedPilotPoints).length > 1 ? updatedPilotPoints[0] : null;
        const newArrivalPilot = (<VoyageRoutePointFeature[]>updatedPilotPoints).length > 0 ? updatedPilotPoints[updatedPilotPoints.length-1] : null;
        const newDeparturePilotEta = newDeparturePilot ? newDeparturePilot.properties.eta : null;
        const newArrivalPilotEta = newArrivalPilot ? newArrivalPilot.properties.eta : null;
        
        // check for pilot ETA conflicts between updatedRoute and plan
        const departureHasChanged = newDeparturePilotEta
                                    && plan.departurePilotEtd > 0
                                    && plan.departurePilotEtd !== Date.parse(newDeparturePilotEta);
        const arrivalHasChanged =   newArrivalPilotEta
                                    && plan.arrivalPilotEta > 0
                                    && plan.arrivalPilotEta !== Date.parse(newArrivalPilotEta);
        
       

        let useNewPilotInfo = false;
        /** This part was moved from voyage-edit-route */
        // new pilot ETAs deviate from plan pilot ETAs
        if (departureHasChanged || arrivalHasChanged) {
            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: "Pilot ETA Conflict",
                bodyText: `${departureHasChanged ? "Departure" : ""} ${departureHasChanged && arrivalHasChanged ? "and" : ""} ${arrivalHasChanged ? "Arrival" : ""} pilot time; ${departureHasChanged && arrivalHasChanged ? "are" : "is"} different from what is defined in Schedule View.
                Do you want to use what is entered here?
                ${departureHasChanged ? `\nDeparture Pilot off in Schedule Editor ${GetFormattedLocalDate(new Date(plan.departurePilotEtd).toISOString())} UTC
                Route Editor input ${GetFormattedLocalDate(newDeparturePilotEta!)} UTC` : ""}
                ${arrivalHasChanged ? `\nArrival Pilot off in Schedule Editor ${GetFormattedLocalDate(new Date(plan.arrivalPilotEta).toISOString())} UTC
                Route Editor input ${GetFormattedLocalDate(newArrivalPilotEta!)} UTC` : ""}`,
                acceptButtonText: "Confirm",
                rejectButtonText: "Cancel"
            });

            const response = await dialog.getResponse();
            if (response === "accept") {
                useNewPilotInfo = true;
            }
        }
        
        // Plan is single source of truth for pilot ETAs, which is used to sync routes pilot ETAs.
        // Therefore, plan should be updated first, then routes
        let planPilotETAsHaveChanged = false;
        if (newArrivalPilotEta || newDeparturePilotEta) {
            planPilotETAsHaveChanged = this._updatePlanPilotInfo(plan, newDeparturePilotEta, newArrivalPilotEta, useNewPilotInfo);
        }
        // no new pilot ETAs, so just update any missing pilot ETAs on the updated route
        else if (((!newDeparturePilotEta && plan.departurePilotEtd !== -1) || (!newArrivalPilotEta && plan.arrivalPilotEta !== -1))) {
            this._updateNewRoutePilotETAs(plan, <VoyageRoutePointFeature>newDeparturePilot, <VoyageRoutePointFeature>newArrivalPilot);
            return;
        }
        if (planPilotETAsHaveChanged) {
            this._updateAllRoutesPilotETAs(plan);
        } else {
            // plan has not changed, but new route might still have pilot ETAs that need to be updated
            this._updateNewRoutePilotETAs(plan, <VoyageRoutePointFeature>newDeparturePilot, <VoyageRoutePointFeature>newArrivalPilot);
        }
    }

    private _updateNewRoutePilotETAs(plan: VoyagePlan, departurePilot: VoyageRoutePointFeature, arrivalPilot: VoyageRoutePointFeature) {
        if (plan.departurePilotEtd !== -1 && departurePilot) {
            departurePilot.properties.eta = new Date(plan.departurePilotEtd).toISOString();
        }
        if (plan.arrivalPilotEta !== -1 && arrivalPilot) {
            arrivalPilot.properties.eta = new Date(plan.arrivalPilotEta).toISOString();
        }
    }

    private _updateAllRoutesPilotETAs(plan: VoyagePlan) {
        for (const route of plan.routes) {
            const pilotIndeces: number[] = [];
            for (let i = 0; i < route.route_geometry.length; i++)
            {
                const feature = route.route_geometry[i];
                if (feature.geometry.type === "Point" && feature.properties.pilotPoint)
                {
                    pilotIndeces.push(i);
                }
            }

            const departurePilot = pilotIndeces.length > 1 ? <VoyageRoutePointFeature>route.route_geometry[pilotIndeces[0]] : null;
            const arrivalPilot = pilotIndeces.length > 0 ? <VoyageRoutePointFeature>route.route_geometry[pilotIndeces[pilotIndeces.length-1]] : null;

            if (departurePilot) {
                if (plan.departurePilotEtd !== -1) {
                    departurePilot.properties.eta = new Date(plan.departurePilotEtd).toISOString();
                } else {
                    delete departurePilot.properties.eta;
                }
            }
            if (arrivalPilot) {
                if (plan.arrivalPilotEta !== -1) {
                    arrivalPilot.properties.eta = new Date(plan.arrivalPilotEta).toISOString();
                } else {
                    delete arrivalPilot.properties.eta;
                }
            }
        }
    }

    private _updatePlanPilotInfo(plan: VoyagePlan, newDeparturePilotEta: string, newArrivalPilotEta: string, useNewPilotInfo: boolean) {
        let planPilotETAsHaveChanged = false;
        // handle missing pilot ETAs on plan
        if (plan.departurePilotEtd < 0 && newDeparturePilotEta) {
            plan.departurePilotEtd = Date.parse(newDeparturePilotEta);
            planPilotETAsHaveChanged = true;
        }
        if (plan.arrivalPilotEta < 0 && newArrivalPilotEta) {
            plan.arrivalPilotEta = Date.parse(newArrivalPilotEta);
            planPilotETAsHaveChanged = true;
        }

        // handle pilot ETA conflicts between route and plan
        if (useNewPilotInfo) {
            if (plan.departurePilotEtd !== -1 && newDeparturePilotEta && plan.departurePilotEtd !== Date.parse(newDeparturePilotEta)) {
                plan.departurePilotEtd = Date.parse(newDeparturePilotEta);
                planPilotETAsHaveChanged = true;
            } 
            if (plan.arrivalPilotEta !== -1 && newArrivalPilotEta && plan.arrivalPilotEta !== Date.parse(newArrivalPilotEta)) {
                plan.arrivalPilotEta = Date.parse(newArrivalPilotEta);
                planPilotETAsHaveChanged = true;
            }
        }

        return planPilotETAsHaveChanged;
    }

     _setRouteViewStatus(routeId: string,scheduleId: string, hide: boolean ): void {

        const latestPlan = VesselPlanningModel!.getCurrentPlan(scheduleId);
        if(!hide) {
            this._hideRouteArray.push(routeId)
        }
        else {
            this._hideRouteArray = this._hideRouteArray.filter(item => item !== routeId);
        } 
        this.dispatchEvent(new Event('route-view-status-changed'));
    }

    _getHiddenRoutes(): string[] {
        return this._hideRouteArray;
    }
};
